// resources/js/components/SensitivityDrawer.jsx
import React, {useEffect, useState} from 'react';
import {Box, Button, Collapse, Divider, Drawer, Grid, IconButton, Typography,} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleSummary from "../zone/ScheduleSummary";
import ZoneSummaryAdvanced from "../zone/ZoneSummaryAdvanced";
import LoadingCard from "../common/LoadingCard";
import ItemSummary from "../item/ItemSummary";
import ItemAlarmManagementSummary from '../item/ItemAlarmManagementSummary';

const ItemsSummaryDrawer = ({allTags, itemApi}) => {
    const [item, setItem] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAlarmManagement, setShowAlarmManagement] = useState(true);

    const onClose = () => {
        setOpen(false);
        setItem(null);
        setShowAlarmManagement(true);
    }
    const zones = item?.zones || [];
    const markerSets = item?.marker_sets || [];

    const getTagById = (id) => allTags.find((tag) => tag.id.toString() == id.toString());
    // fetch item data when an event is triggered on window 'items:summary' with payload itemId
    useEffect(() => {
        const handleSummary = (event) => {
            const {itemId} = event.detail;
            setLoading(true);
            setOpen(true);
            setShowAlarmManagement(true);
            let itemUrl = itemApi.replace('item:', itemId);
            fetch(itemUrl).then((response) => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            }).then((data) => {
                setItem(data.item);
                setLoading(false);
            }).catch((error) => {
                console.error('Error fetching item data:', error);
                setLoading(false);
                setError('Item data not able to load');
            });
        }

        window.addEventListener('items:summary', handleSummary);

        return () => {
            window.removeEventListener('items:summary', handleSummary);
        }

    }, []);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {width: '40%'},
            }}
        >
            <Box sx={{p: 2}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 2}}>
                    <Typography variant="h6">Item summary {item?.name ?? ""}</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon/>
                    </IconButton>
                </Box>
                {error && (
                    <Box sx={{mb: 2}}>
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    </Box>
                )}
                <Box sx={{mb: 2}}>
                    <ItemSummary item={item} allTags={allTags}/>
                </Box>
                {loading ? (
                    <LoadingCard/>
                ) : (<Box sx={{mb: 4}}>
                    {zones.map((zone, index) => (
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={12}>
                                <Typography variant="h5" fontWeight={500}>
                                    Zone {index + 1}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <ZoneSummaryAdvanced
                                    advancedToggle={zone.advanced}
                                    countBasic={zone.tags.map((tag) => tag.id)}
                                    alarmsBasic={zone.alarms.map((alarm) => alarm.id)}
                                    modelAlarms={zone.advanced ? zone.advanced_settings?.model_alarms : []}
                                    getTagById={getTagById}
                                    advanced={zone.advanced_settings}
                                    expandedDefault={false}
                                    markerSets={markerSets}
                                    gpioConfig={zone?.meta?.gpio ?? zone?.gpio ?? {}}
                                    trafficLights={(item?.traffic_lights || []).filter((light) => (zone?.meta?.traffic_light_ids || []).map(Number).includes(Number(light.id)))}
                                />

                            </Grid>
                            <Grid item xs={12} md={12}>
                                <ScheduleSummary schedule={zone.armed || []}/>
                            </Grid>
                        </Grid>
                    ))}
                </Box>)}

                {!loading && (
                    <Box sx={{ mb: 2 }}>
                        <Divider sx={{ mb: 1.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2">Alarm Management Summary</Typography>
                            <IconButton size="small" onClick={() => setShowAlarmManagement((value) => !value)}>
                                {showAlarmManagement ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                            </IconButton>
                        </Box>
                        <Collapse in={showAlarmManagement} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1 }}>
                                <ItemAlarmManagementSummary item={item} allTags={allTags}/>
                            </Box>
                        </Collapse>
                    </Box>
                )}

                <Button variant="contained" color="error" onClick={onClose} sx={{mt: 2}}>
                    Close
                </Button>
            </Box>
        </Drawer>
    );
};

export default ItemsSummaryDrawer;
